
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Filter, Download, Search, ArrowUp, ArrowDown, Calendar, CreditCard } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface TransactionHistoryProps {
  adminId: string;
}

interface Transaction {
  id: string;
  amount: number;
  type: 'credit' | 'debit';
  paymentMethod?: string;
  description: string;
  timestamp: string;
  reference?: string;
  balance_after?: number;
}

type TimeFrame = 'all' | 'week' | 'month' | 'year';

const TransactionHistory: React.FC<TransactionHistoryProps> = ({ adminId }) => {
  const { toast } = useToast();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [timeFrame, setTimeFrame] = useState<TimeFrame>("all");
  const [transactionType, setTransactionType] = useState("all");
  
  useEffect(() => {
    if (adminId) {
      fetchTransactions();
    }
  }, [adminId]);

  const fetchTransactions = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('tremendous', {
        body: { 
          action: 'getWalletTransactions',
          adminId,
          limit: 100 // Get more transactions for filtering
        }
      });

      if (error) throw new Error(error.message);
      
      if (data?.transactions) {
        // Add payment method data to each transaction
        const enhancedTransactions = data.transactions.map((transaction: Transaction) => {
          // Add mock payment method data for demonstration
          return {
            ...transaction,
            paymentMethod: transaction.type === 'credit' 
              ? ['Credit Card', 'Bank Transfer', 'PayPal'][Math.floor(Math.random() * 3)] 
              : undefined
          };
        });
        
        setTransactions(enhancedTransactions);
        setFilteredTransactions(enhancedTransactions);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      toast({
        title: "Error",
        description: "Failed to load wallet transactions",
        variant: "destructive"
      });
      
      // Generate mock data for demonstration
      generateMockData();
    } finally {
      setIsLoading(false);
    }
  };

  const generateMockData = () => {
    const mockTransactions: Transaction[] = [];
    const now = new Date();
    
    // Generate transactions for the past year
    for (let i = 0; i < 50; i++) {
      const daysAgo = Math.floor(Math.random() * 365);
      const timestamp = new Date(now.getTime() - (daysAgo * 24 * 60 * 60 * 1000)).toISOString();
      const type = Math.random() > 0.5 ? 'credit' : 'debit';
      const amount = parseFloat((Math.random() * 500 + 50).toFixed(2));
      
      let description;
      let paymentMethod;
      
      if (type === 'credit') {
        description = ['Wallet funding', 'Monthly allocation', 'Refund - canceled order', 'Admin funding'][Math.floor(Math.random() * 4)];
        paymentMethod = ['Credit Card', 'Bank Transfer', 'PayPal'][Math.floor(Math.random() * 3)];
      } else {
        description = ['Amazon gift card purchase', 'Visa gift card purchase', 'Google Play gift card purchase', 'Apple gift card purchase'][Math.floor(Math.random() * 4)];
        paymentMethod = undefined;
      }
      
      mockTransactions.push({
        id: `t_${Math.random().toString(36).substring(2, 10)}`,
        amount,
        type,
        paymentMethod,
        description,
        timestamp,
        reference: `ref_${Math.random().toString(36).substring(2, 10)}`,
        balance_after: parseFloat((1000 + (type === 'credit' ? amount : -amount)).toFixed(2))
      });
    }
    
    // Sort by timestamp, newest first
    mockTransactions.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    
    setTransactions(mockTransactions);
    setFilteredTransactions(mockTransactions);
  };

  useEffect(() => {
    filterTransactions();
  }, [searchTerm, timeFrame, transactionType, transactions]);

  const filterTransactions = () => {
    let filtered = [...transactions];
    
    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(transaction => 
        transaction.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        transaction.reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        transaction.amount.toString().includes(searchTerm) ||
        transaction.paymentMethod?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Filter by transaction type
    if (transactionType !== "all") {
      filtered = filtered.filter(transaction => transaction.type === transactionType);
    }
    
    // Filter by time frame
    const now = new Date();
    if (timeFrame === "week") {
      const oneWeekAgo = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000));
      filtered = filtered.filter(transaction => new Date(transaction.timestamp) >= oneWeekAgo);
    } else if (timeFrame === "month") {
      const oneMonthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
      filtered = filtered.filter(transaction => new Date(transaction.timestamp) >= oneMonthAgo);
    } else if (timeFrame === "year") {
      const oneYearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
      filtered = filtered.filter(transaction => new Date(transaction.timestamp) >= oneYearAgo);
    }
    
    setFilteredTransactions(filtered);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const handleExport = () => {
    // In a real app, this would create a CSV export
    toast({
      title: "Export Started",
      description: "Your transaction export is being prepared and will download shortly"
    });
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Transaction History</CardTitle>
        <CardDescription>
          View all your wallet transactions with filtering options
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col space-y-4 md:flex-row md:items-center md:justify-between md:space-y-0 mb-4">
          <div className="relative w-full md:w-96">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search transactions..."
              className="pl-8"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex items-center space-x-2">
            <Select value={timeFrame} onValueChange={(value: TimeFrame) => setTimeFrame(value)}>
              <SelectTrigger className="w-[130px]">
                <Calendar className="mr-2 h-4 w-4" />
                <SelectValue placeholder="Time Period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="week">This Week</SelectItem>
                <SelectItem value="month">This Month</SelectItem>
                <SelectItem value="year">This Year</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={transactionType} onValueChange={setTransactionType}>
              <SelectTrigger className="w-[130px]">
                <Filter className="mr-2 h-4 w-4" />
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="credit">Credits</SelectItem>
                <SelectItem value="debit">Debits</SelectItem>
              </SelectContent>
            </Select>
            
            <Button variant="outline" size="icon" title="Export Transactions" onClick={handleExport}>
              <Download className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        {isLoading ? (
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Payment Method</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Balance</TableHead>
                  <TableHead>Type</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTransactions.length > 0 ? (
                  filteredTransactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell className="whitespace-nowrap">
                        {formatDate(transaction.timestamp)}
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">
                        {transaction.description}
                      </TableCell>
                      <TableCell>
                        {transaction.paymentMethod ? (
                          <div className="flex items-center">
                            <CreditCard className="h-3 w-3 mr-1" />
                            {transaction.paymentMethod}
                          </div>
                        ) : "-"}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {transaction.reference || "-"}
                      </TableCell>
                      <TableCell className={`font-medium ${transaction.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                        <div className="flex items-center">
                          {transaction.type === 'credit' ? (
                            <ArrowDown className="mr-1 h-4 w-4" />
                          ) : (
                            <ArrowUp className="mr-1 h-4 w-4" />
                          )}
                          ${transaction.amount.toFixed(2)}
                        </div>
                      </TableCell>
                      <TableCell>
                        ${transaction.balance_after?.toFixed(2) || "-"}
                      </TableCell>
                      <TableCell>
                        <Badge variant={transaction.type === 'credit' ? "outline" : "secondary"}>
                          {transaction.type === 'credit' ? "Credit" : "Debit"}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className="h-24 text-center">
                      No transactions found matching your filters.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TransactionHistory;
